<script setup>
import { tv } from 'tailwind-variants';

/**
 * @typedef {object} EditorBoxProps
 * @property {string} [as='div'] - The HTML element to render the component as.
 * @property {('default'|'magic'|'caution'|'error'|'unstyled')} [tone='default'] - The visual tone of the editor box.
 * @property {boolean} [isFocused=false] - Whether the editor box is currently focused.
 * @property {boolean} [isDisabled=false] - Whether the editor box is disabled.
 * @property {boolean} [isError=false] - Whether the editor box is in error state.
 */

/** @type {EditorBoxProps} */
const props = defineProps({
  as: {
    type: String,
    default: 'div',
  },
  tone: {
    type: String,
    default: 'default',
  },
  isFocused: {
    type: Boolean,
    default: false,
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
  isError: {
    type: Boolean,
    default: false,
  },
});

const editorBoxRecipe = tv({
  base: 'cs-editor-box relative w-full border rounded-lg',
  variants: {
    tone: {
      default: [
        'border-brand-gray-12',
        'bg-brand-background',
        'hover:bg-brand-gray-3',
        'dark:bg-brand-gray-16',
        'hover:border-brand-gray-13',
        'dark:hover:border-brand-gray-14',
        'data-[focused=true]:border-brand-gray-16',
        'data-[focused=true]:bg-brand-gray-3',
      ],
      magic: ['bg-brand-surface-magic', 'border-brand-border-magic-secondary'],
      caution: [
        'bg-brand-surface-caution',
        'border-brand-gray-12',
        'dark:border-brand-yellow-14',
        'hover:bg-brand-surface-caution',
        'hover:border-brand-gray-13',
        'dark:hover:border-brand-yellow-13',
        'data-[focused=true]:border-brand-gray-16',
        'data-[focused=true]:bg-brand-surface-caution',
      ],
      error: [
        'bg-brand-red-4',
        'dark:bg-brand-red-16',
        'hover:bg-brand-red-4',
        'hover:dark:bg-brand-red-16',
        'border-brand-red-12',
        'dark:border-brand-red-13',
        'hover:border-brand-red-13',
        'dark:hover:border-brand-red-14',
        'data-[focused=true]:border-brand-red-16',
        'data-[focused=true]:bg-brand-red-4',
      ],
      unstyled: ['border-none', 'rounded-none'],
    },
    isDisabled: {
      true: [
        'cursor-not-allowed bg-brand-black-alpha-5 dark:bg-brand-gray-14',
        'border !border-transparent',
      ],
      false: '',
    },
    isUnstyled: {
      true: 'border-none rounded-none',
      false: '',
    },
  },
  defaultVariants: {
    isFocused: false,
    isDisabled: false,
    tone: 'default',
  },
});
</script>

<template>
  <component
    :is="props.as"
    data-component="CsEditorBox"
    :data-disabled="props.isDisabled"
    :data-focused="props.isFocused"
    :class="
      editorBoxRecipe({
        tone: props.tone,
        isFocused: props.isFocused,
        isDisabled: props.isDisabled,
        isUnstyled: props.isUnstyled,
      })
    "
  >
    <slot />
  </component>
</template>

<style>
.cs-editor-box .tiptap {
  @apply text-brand-text;
}

.cs-editor-box .ProseMirror {
  padding: 0 !important;
}

.cs-editor-box .tiptap:focus {
  outline: none;
}

.cs-editor-box[data-disabled='true'] .tiptap,
.cs-editor-box[data-disabled='true'] .tiptap * {
  @apply text-brand-gray-11 dark:text-brand-gray-14 cursor-not-allowed;
}

.cs-editor-box .tiptap p {
  font-size: 14px;
  line-height: 1.25rem;
  margin-top: 10px;
  margin-bottom: 0px;
}
.cs-editor-box .tiptap p:first-child {
  margin-top: 0;
  margin-bottom: 0px;
}

.cs-editor-box .tiptap p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  height: 0;
  pointer-events: none;
  @apply text-brand-text-secondary;
}

.cs-editor-box[data-disabled='true']
  .tiptap
  p.is-editor-empty:first-child::before {
  @apply text-brand-gray-11 dark:text-brand-gray-14 cursor-not-allowed;
}

.cs-editor-box .tiptap h1 {
  font-size: 24px;
  font-weight: 600;
  line-height: 1.5;
}
.cs-editor-box .tiptap h2 {
  font-size: 20px;
  font-weight: 600;
  line-height: 1.5;
}
.cs-editor-box .tiptap h3 {
  font-size: 18px;
  font-weight: 600;
  line-height: 1.5;
}
.cs-editor-box .tiptap ul {
  list-style-type: disc;
  padding-left: 1.5em;
  margin: 0.5em 0;
}
.cs-editor-box .tiptap ol {
  list-style-type: decimal;
  padding-left: 1.5em;
  margin: 0.5em 0;
}
.cs-editor-box .tiptap li {
  font-size: 14px;
  margin-bottom: 0.25em;
}
.cs-editor-box .tiptap li p {
  margin: 0;
  display: inline;
}
.cs-editor-box .tiptap a {
  color: #3b82f6;
  text-decoration: underline;
  cursor: pointer;
}
.cs-editor-box .tiptap code {
  background-color: #f1f5f9;
  border-radius: 0.25em;
  box-decoration-break: clone;
  color: #334155;
  font-family: monospace;
  font-size: 0.9em;
  padding: 0.25em 0.4em;
}

.cs-editor-box .tiptap mark {
  border-radius: 0.1rem;
  box-decoration-break: clone;
  padding: 0.02rem 0.2rem;
}

.cs-editor-box .tiptap-image.ProseMirror-selectednode {
  outline: none;
}
</style>
